import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, Plus, Trash2, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UploadPage() {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  
  // Manual form state
  const [manualForm, setManualForm] = useState({
    name: "",
    category: "",
    base_spirit: "",
    garnish: "",
    glassware: "",
    side_elements: "",
    method: "",
    notes: "",
    ingredients: [{ name: "", measurement: "", brand: "" }]
  });
  const [submitting, setSubmitting] = useState(false);

  const handleFileUpload = async (file) => {
    if (!file) return;
    if (!file.name.endsWith('.csv')) {
      toast.error("Please upload a CSV file");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    setUploading(true);
    setUploadResult(null);
    try {
      const response = await axios.post(`${API}/cocktails/upload-csv`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setUploadResult(response.data);
      toast.success(`Successfully uploaded ${response.data.count} cocktails`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload file");
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFileUpload(file);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    handleFileUpload(file);
  };

  const addIngredient = () => {
    setManualForm(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, { name: "", measurement: "", brand: "" }]
    }));
  };

  const removeIngredient = (index) => {
    setManualForm(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index)
    }));
  };

  const updateIngredient = (index, field, value) => {
    setManualForm(prev => ({
      ...prev,
      ingredients: prev.ingredients.map((ing, i) => 
        i === index ? { ...ing, [field]: value } : ing
      )
    }));
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    
    if (!manualForm.name || !manualForm.category || !manualForm.base_spirit) {
      toast.error("Please fill in required fields");
      return;
    }

    const validIngredients = manualForm.ingredients.filter(ing => ing.name && ing.measurement);
    if (validIngredients.length === 0) {
      toast.error("Please add at least one ingredient");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/cocktails`, {
        ...manualForm,
        ingredients: validIngredients
      });
      toast.success("Cocktail added successfully");
      setManualForm({
        name: "",
        category: "",
        base_spirit: "",
        garnish: "",
        glassware: "",
        side_elements: "",
        method: "",
        notes: "",
        ingredients: [{ name: "", measurement: "", brand: "" }]
      });
    } catch (error) {
      toast.error("Failed to add cocktail");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="upload-page" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Upload Specs</h1>
        <p className="text-[hsl(var(--muted-foreground))] mt-2">
          Import cocktails via CSV or add manually
        </p>
      </div>

      <Tabs defaultValue="csv" className="space-y-6">
        <TabsList className="bg-[hsl(var(--secondary))]">
          <TabsTrigger value="csv" data-testid="tab-csv">CSV Upload</TabsTrigger>
          <TabsTrigger value="manual" data-testid="tab-manual">Manual Entry</TabsTrigger>
        </TabsList>

        {/* CSV Upload Tab */}
        <TabsContent value="csv" className="space-y-6">
          <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileSpreadsheet className="text-[hsl(var(--primary))]" size={24} />
                Upload CSV File
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div
                className={`upload-zone ${dragOver ? 'drag-over' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                data-testid="upload-dropzone"
              >
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="csv-upload"
                  data-testid="csv-file-input"
                />
                <label htmlFor="csv-upload" className="cursor-pointer">
                  <Upload className="mx-auto text-[hsl(var(--muted-foreground))] mb-4" size={48} />
                  <p className="text-lg font-medium mb-2">
                    {uploading ? "Uploading..." : "Drop your CSV file here"}
                  </p>
                  <p className="text-sm text-[hsl(var(--muted-foreground))]">
                    or click to browse
                  </p>
                </label>
              </div>

              {uploadResult && (
                <div className="mt-4 p-4 rounded-lg bg-green-500/10 border border-green-500/20 flex items-center gap-3" data-testid="upload-success">
                  <CheckCircle className="text-green-500" size={24} />
                  <p className="text-green-400">{uploadResult.message}</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <CardHeader>
              <CardTitle>CSV Format Guide</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                Your CSV should have the following columns:
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[hsl(var(--border))]">
                      <th className="text-left py-2 pr-4 text-[hsl(var(--primary))]">Column</th>
                      <th className="text-left py-2 text-[hsl(var(--primary))]">Format</th>
                    </tr>
                  </thead>
                  <tbody className="text-[hsl(var(--muted-foreground))]">
                    <tr className="border-b border-[hsl(var(--border))]">
                      <td className="py-2 pr-4 font-medium text-[hsl(var(--foreground))]">Name</td>
                      <td className="py-2">Cocktail name (required)</td>
                    </tr>
                    <tr className="border-b border-[hsl(var(--border))]">
                      <td className="py-2 pr-4 font-medium text-[hsl(var(--foreground))]">Category</td>
                      <td className="py-2">e.g., Classic, Tiki, Modern</td>
                    </tr>
                    <tr className="border-b border-[hsl(var(--border))]">
                      <td className="py-2 pr-4 font-medium text-[hsl(var(--foreground))]">Base Spirit</td>
                      <td className="py-2">e.g., Gin, Vodka, Rum</td>
                    </tr>
                    <tr className="border-b border-[hsl(var(--border))]">
                      <td className="py-2 pr-4 font-medium text-[hsl(var(--foreground))]">Ingredients</td>
                      <td className="py-2 mono text-xs">name,measure,brand; name2,measure2</td>
                    </tr>
                    <tr className="border-b border-[hsl(var(--border))]">
                      <td className="py-2 pr-4 font-medium text-[hsl(var(--foreground))]">Garnish</td>
                      <td className="py-2">e.g., Lime wheel</td>
                    </tr>
                    <tr className="border-b border-[hsl(var(--border))]">
                      <td className="py-2 pr-4 font-medium text-[hsl(var(--foreground))]">Glassware</td>
                      <td className="py-2">e.g., Coupe, Highball</td>
                    </tr>
                    <tr className="border-b border-[hsl(var(--border))]">
                      <td className="py-2 pr-4 font-medium text-[hsl(var(--foreground))]">Side Elements</td>
                      <td className="py-2">e.g., Shot of espresso</td>
                    </tr>
                    <tr className="border-b border-[hsl(var(--border))]">
                      <td className="py-2 pr-4 font-medium text-[hsl(var(--foreground))]">Method</td>
                      <td className="py-2">Preparation instructions</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-4 font-medium text-[hsl(var(--foreground))]">Notes</td>
                      <td className="py-2">Additional notes</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Manual Entry Tab */}
        <TabsContent value="manual">
          <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plus className="text-[hsl(var(--primary))]" size={24} />
                Add Cocktail Manually
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleManualSubmit} className="space-y-6" data-testid="manual-cocktail-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={manualForm.name}
                      onChange={(e) => setManualForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Negroni"
                      className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]"
                      data-testid="input-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="category">Category *</Label>
                    <Input
                      id="category"
                      value={manualForm.category}
                      onChange={(e) => setManualForm(prev => ({ ...prev, category: e.target.value }))}
                      placeholder="e.g., Classic"
                      className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]"
                      data-testid="input-category"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="base_spirit">Base Spirit *</Label>
                    <Input
                      id="base_spirit"
                      value={manualForm.base_spirit}
                      onChange={(e) => setManualForm(prev => ({ ...prev, base_spirit: e.target.value }))}
                      placeholder="e.g., Gin"
                      className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]"
                      data-testid="input-base-spirit"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="glassware">Glassware</Label>
                    <Input
                      id="glassware"
                      value={manualForm.glassware}
                      onChange={(e) => setManualForm(prev => ({ ...prev, glassware: e.target.value }))}
                      placeholder="e.g., Rocks"
                      className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]"
                      data-testid="input-glassware"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="garnish">Garnish</Label>
                    <Input
                      id="garnish"
                      value={manualForm.garnish}
                      onChange={(e) => setManualForm(prev => ({ ...prev, garnish: e.target.value }))}
                      placeholder="e.g., Orange peel"
                      className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]"
                      data-testid="input-garnish"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="side_elements">Side Elements</Label>
                    <Input
                      id="side_elements"
                      value={manualForm.side_elements}
                      onChange={(e) => setManualForm(prev => ({ ...prev, side_elements: e.target.value }))}
                      placeholder="e.g., Espresso shot"
                      className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]"
                      data-testid="input-side-elements"
                    />
                  </div>
                </div>

                {/* Ingredients */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Ingredients *</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addIngredient}
                      data-testid="add-ingredient-btn"
                    >
                      <Plus size={16} className="mr-1" /> Add
                    </Button>
                  </div>
                  {manualForm.ingredients.map((ing, idx) => (
                    <div key={idx} className="flex gap-2 items-start" data-testid={`ingredient-row-${idx}`}>
                      <Input
                        placeholder="Ingredient"
                        value={ing.name}
                        onChange={(e) => updateIngredient(idx, "name", e.target.value)}
                        className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))] flex-1"
                        data-testid={`ingredient-name-${idx}`}
                      />
                      <Input
                        placeholder="Measure"
                        value={ing.measurement}
                        onChange={(e) => updateIngredient(idx, "measurement", e.target.value)}
                        className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))] w-28"
                        data-testid={`ingredient-measure-${idx}`}
                      />
                      <Input
                        placeholder="Brand (opt)"
                        value={ing.brand}
                        onChange={(e) => updateIngredient(idx, "brand", e.target.value)}
                        className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))] w-32"
                        data-testid={`ingredient-brand-${idx}`}
                      />
                      {manualForm.ingredients.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeIngredient(idx)}
                          className="text-red-500 hover:text-red-400"
                          data-testid={`remove-ingredient-${idx}`}
                        >
                          <Trash2 size={18} />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="method">Method</Label>
                  <Textarea
                    id="method"
                    value={manualForm.method}
                    onChange={(e) => setManualForm(prev => ({ ...prev, method: e.target.value }))}
                    placeholder="Preparation instructions..."
                    className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))] min-h-[100px]"
                    data-testid="input-method"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={manualForm.notes}
                    onChange={(e) => setManualForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Additional notes..."
                    className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]"
                    data-testid="input-notes"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="btn-glow bg-[hsl(var(--primary))] text-black hover:bg-[hsl(var(--primary))]/90"
                  data-testid="submit-cocktail-btn"
                >
                  {submitting ? "Adding..." : "Add Cocktail"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
